<template>
  <div class="manage-accounts-page">
    <div class="page-header">
      <h1>Manage Accounts</h1>
      <div class="header-controls">
        <button class="add-btn" @click="openAddModal">+ Add Account</button>
      </div>
    </div>

    <div class="accounts-table">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Created Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="account in accounts" :key="account.id">
            <td>{{ account.name }}</td>
            <td>{{ account.email }}</td>
            <td><span class="role-badge" v-for="r in account.role" :key="r" :class="r">{{ r }}</span></td>
            <td>{{ account.created_at }}</td>
            <td class="actions">
              <button class="edit-btn" @click="openEditModal(account)">Edit</button>
              <button class="delete-btn" @click="deleteAccount(account.id)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <Dialog 
      v-model:visible="showModal" 
      :header="isEditing ? 'Edit Account' : 'Add Account'" 
      :modal="true" 
      :closable="false" 
      :dismissable-mask="false"
      :style="{width: '500px'}"
    >
      <form @submit.prevent="saveAccount" class="modal-form">
        <div class="form-grid">
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="name"
                v-model="form.name" 
                :class="{ 'p-invalid': !form.name && formSubmitted }"
                required 
              />
              <label for="name">Name *</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <InputText 
                id="email"
                v-model="form.email" 
                type="email" 
                :class="{ 'p-invalid': !form.email && formSubmitted }"
                required 
              />
              <label for="email">Email Address *</label>
            </IftaLabel>
          </div>
          
          <div class="form-group">
            <IftaLabel>
              <MultiSelect 
                id="role"
                v-model="form.role" 
                :options="roleOptions" 
                optionLabel="label" 
                optionValue="value" 
                :class="{ 'p-invalid': (!form.role || form.role.length === 0) && formSubmitted }"
                display="chip"
                required 
              />
              <label for="role">Role *</label>
            </IftaLabel>
          </div>
          
          <div class="form-group" v-if="!isEditing">
            <div class="info-card">
              <i class="pi pi-info-circle"></i>
              <span>A setup email will be sent to the user to create their password.</span>
            </div>
          </div>
        </div>
        
        <div class="modal-actions">
          <Button 
            type="button" 
            label="Cancel" 
            severity="secondary" 
            icon="pi pi-times"
            @click="closeModal" 
          />
          <Button 
            type="submit" 
            :label="isEditing ? 'Update Account' : 'Create Account'" 
            icon="pi pi-check"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import api from '../axios.js'

export default {
  data() {
    return {
      accounts: [],
      showModal: false,
      currentPage: 1,
      isEditing: false,
      saving: false,
      formSubmitted: false,
      form: {
        id: null,
        name: '',
        email: '',
        role: []
      },
      roleOptions: [
        { label: 'Admin', value: 'admin' },
        { label: 'Encoder', value: 'encoder' },
        { label: 'Viewer', value: 'viewer' },
        { label: 'Account Officer', value: 'account-officer' },
        { label: 'PLLG Staff', value: 'pllg-staff' },
        { label: 'PLLG DC', value: 'pllg-dc' },

        { label: 'LO DC', value: 'lld-dc' },

      ]
    }
  },
   computed: {
    totalPages() {
      return Math.ceil(this.accounts.length / this.perPage)
    },
    paginatedAccounts() {
      const start = (this.currentPage - 1) * this.perPage
      return this.accounts.slice(start, start + this.perPage)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      let start = Math.max(1, this.currentPage - 2)
      let end = Math.min(total, this.currentPage + 2)

      if (this.currentPage <= 3) {
        end = Math.min(total, 5)
      }
      if (this.currentPage >= total - 2) {
        start = Math.max(1, total - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  methods: {
      async loadAccounts() {
      try {
        const response = await api.get('/users')
        this.accounts = response.data
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages
        }
      } catch (error) {
        console.error('Error loading accounts:', error)
        this.accounts = []
      }
    },
    openAddModal() {
      this.isEditing = false
      this.form = { id: null, name: '', email: '', role: [] }
      this.showModal = true
    },
    openEditModal(account) {
      this.isEditing = true
      this.form = { ...account }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.formSubmitted = false
      this.saving = false
      this.form = { id: null, name: '', email: '', role: [] }
    },
    async saveAccount() {
      this.formSubmitted = true
      
      if (!this.form.name || !this.form.email || !this.form.role || this.form.role.length === 0) {
        return
      }
      
      const result = await Swal.fire({
        title: 'Confirm Action',
        text: `Are you sure you want to ${this.isEditing ? 'update' : 'create'} this account?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.isEditing ? 'Update' : 'Create',
        cancelButtonText: 'Cancel',
        target: document.body
      })
      
      if (!result.isConfirmed) {
        return
      }
      
      try {
        this.saving = true
        const data = {
          name: this.form.name,
          email: this.form.email,
          role: this.form.role
        }
        
        if (this.isEditing) {
          await api.put(`/users/${this.form.id}`, data)
        } else {
          await api.post('/users', data)
        }
        
        this.closeModal()
        this.loadAccounts()
        
        Swal.fire({
          title: 'Success!',
          text: `Account ${this.isEditing ? 'updated' : 'created'} successfully`,
          icon: 'success',
          confirmButtonText: 'OK',
          target: document.body
        })
      } catch (error) {
        console.error('Error saving account:', error)
      } finally {
        this.saving = false
      }
    },
    async deleteAccount(id) {
      const result = await Swal.fire({
        title: 'Delete Account',
        text: 'Are you sure you want to delete this account?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#ef4444',
        target: document.body
      })
      
      if (result.isConfirmed) {
        try {
          await api.delete(`/users/${id}`)
          this.loadAccounts()
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Account has been deleted successfully',
            icon: 'success',
            confirmButtonText: 'OK',
            target: document.body
          })
        } catch (error) {
          console.error('Error deleting account:', error)
        }
      }
    }
  },

  mounted() {
    this.loadAccounts()
  }
}
</script>

<style scoped>
.manage-accounts-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.add-btn {
  background: #1e3a8a;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #1e40af;
}

.accounts-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(30, 58, 138, 0.1);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
  color: white;
}

th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody tr {
  border-bottom: 1px solid #e5e7eb;
}

tbody tr:hover {
  background: #f8fafc;
}

td {
  padding: 1rem;
  color: #374151;
}

td:first-child {
  font-weight: 600;
  color: #1e3a8a;
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-right: 0.25rem;
}

.role-badge.admin {
  background: #fee2e2;
  color: #dc2626;
}

.role-badge.viewer {
  background: #dbeafe;
  color: #2563eb;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.edit-btn, .delete-btn {
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.delete-btn {
  background: #ef4444;
  color: white;
}

.delete-btn:hover {
  background: #dc2626;
}

.modal-form {
  padding: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}



.form-group .p-inputtext,
.form-group .p-select {
  width: 100% !important;
  box-sizing: border-box;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
  color: #1e40af;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  border-left: 4px solid #3b82f6;
}

.info-card i {
  font-size: 1.1rem;
  color: #3b82f6;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}



.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 1rem;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled):not(.active) {
  background: #f3f4f6;
  border-color: #1e3a8a;
  color: #1e3a8a;
}

.page-btn.active {
  background: #1e3a8a;
  border-color: #1e3a8a;
  color: white;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-info {
  margin-left: 1rem;
  font-size: 0.85rem;
  color: #6b7280;
}

@media (max-width: 768px) {
  .manage-accounts-page {
    padding: 1rem;
  }
  
  .accounts-table {
    overflow-x: auto;
  }
  
  table {
    min-width: 700px;
  }
  
  th, td {
    padding: 0.75rem 0.5rem;
    font-size: 0.85rem;
  }
}
</style>